import { api } from "../api";
import { withZoneNames } from "./zoneExport";
import { ApiError } from "../api/client";
import { labForWire, haneyPointFlags, type LabOrderConfig } from "./labTests";
import { registerTarget } from "../stores/targetSamplingStore";
import { coresForSave, zoneCoresLayerId } from "./zoneCores";
import { orderPointId } from "./targetPointLabel";
import { useZoneCoreStore } from "../stores/zoneCoreStore";
import { useLayerStore } from "../stores/layerStore";
import {
  automapIdForMode,
  campaignKind,
  type CampaignField,
  type CampaignMode,
  type Captured,
} from "../stores/samplingCampaign";
import type { SamplingMode } from "../stores/samplingConfirm";
import type { ManifestLayer } from "../types/contract";

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : "Something went wrong.";
}

export interface FieldOrderResult {
  field: CampaignField;
  ok: boolean;
  error?: string;
  layerId?: string; // the Target Sampling Layer to reveal on Save-only (points → target-points-*, zones → 'zones')
}

export interface ResolvedFC {
  field: CampaignField;
  fc: GeoJSON.FeatureCollection | null;
  done?: boolean; // set once persisted, so a retry after a PARTIAL failure never re-commits a succeeded field
  layerId?: string;
}

// Resolve a field's AutoMap design FeatureCollection (inline OR fetched from its signed URL) — the same
// inline-or-signed resolution the preview maps use. Returns null if the field has no such AutoMap yet.
export async function fetchAutomapFC(
  fieldId: string,
  automapId: string,
): Promise<GeoJSON.FeatureCollection | null> {
  let l: ManifestLayer;
  try {
    l = await api.fetchLayerDetail(fieldId, automapId);
  } catch {
    return null;
  }
  const inline = l.inline as GeoJSON.GeoJSON | undefined;
  const g =
    inline ??
    (l.signed_url
      ? await fetch(l.signed_url)
          .then((r) => r.json())
          .catch(() => null)
      : null);
  return g && (g as GeoJSON.GeoJSON).type === "FeatureCollection"
    ? (g as GeoJSON.FeatureCollection)
    : null;
}

// captured points → a points FeatureCollection (num carried in properties for the P-<num> id)
export function pointsToFC(
  points: { num: number; lng: number; lat: number }[],
): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: points.map((p) => ({
      type: "Feature",
      properties: { num: p.num },
      geometry: { type: "Point", coordinates: [p.lng, p.lat] },
    })),
  };
}

/*
 *
 * Single-field never showed it because that path passes the card's own resolved FC straight
 * through. The card is now the single source: what you clicked is what gets ordered, by
 * construction.
 */
export async function resolvePresetFCs(
  fields: CampaignField[],
  mode: CampaignMode,
  schoolAutomapId?: string,
): Promise<ResolvedFC[]> {
  const automapId = schoolAutomapId ?? automapIdForMode(mode);
  const out: ResolvedFC[] = [];
  for (const f of fields) {
    let fc: GeoJSON.FeatureCollection | null;
    try {
      fc = await fetchAutomapFC(f.field_id, automapId);
    } catch {
      fc = null;
    }
    out.push({ field: f, fc });
  }
  return out;
}

// CUSTOM stepper: resolve each field's captured design (or its AutoMap default for a field never opened).
export async function resolveCampaignFCs(
  fields: CampaignField[],
  mode: CampaignMode,
  captured: Record<string, Captured>,
): Promise<ResolvedFC[]> {
  const out: ResolvedFC[] = [];
  for (const f of fields) {
    const cap = captured[f.field_id];
    let fc: GeoJSON.FeatureCollection | null = cap
      ? cap.kind === "points"
        ? pointsToFC(cap.points)
        : cap.fc
      : null;
    if (!fc || !fc.features?.length) {
      try {
        fc = await fetchAutomapFC(f.field_id, automapIdForMode(mode));
      } catch {
        fc = null;
      }
    }
    out.push({ field: f, fc });
  }
  return out;
}

// Persist ONE field's design in the chosen mode. Points: order → orderSampling (geometry in the payload)
// save → saveSamplePoints; either way register the client Target Sampling Layer. Zones: always saveZones
// and order → also orderSampling.
// Returns the Target Sampling Layer id to reveal on Save-only.
export async function persistFieldFC(
  field: CampaignField,
  kind: "points" | "zones",
  fc: GeoJSON.FeatureCollection,
  lab: LabOrderConfig,
  action: SamplingMode,
  when: string,
): Promise<string> {
  if (kind === "points") {
    // sort by num so the "1 out of N" Haney designation is deterministic + matches the in-map editor path
    const sorted = [...fc.features].sort(
      (a, b) =>
        ((a.properties?.num as number) ?? 0) -
        ((b.properties?.num as number) ?? 0),
    );
    const haneyFlags =
      action === "order" && lab.haney.enabled
        ? haneyPointFlags(sorted.length, lab.haney)
        : null;
    const points = sorted.map((f, i) => {
      const [lon, lat] = (f.geometry as GeoJSON.Point).coordinates as [
        number,
        number,
      ];
      return {
        point_id: orderPointId(
          f.properties as {
            point_id?: unknown;
            num?: unknown;
            id_source?: unknown;
          },
          i,
        ),
        lon,
        lat,
        ...(haneyFlags ? { haney: haneyFlags[i] } : {}),
      };
    });
    if (action === "order")
      await api.orderSampling(field.field_id, {
        config: { kind: "points", points, lab: labForWire(lab) },
      });
    else await api.saveSamplePoints(field.field_id, fc);
    return registerTarget(
      field.field_id,
      "points",
      fc,
      action === "order",
      when,
    );
  }
  // Applied here because this is the ONE choke point all zone saves cross (customize commits AND
  // served-automap default orders), so nothing can be persisted nameless.
  const named = withZoneNames(fc);
  const cores = coresForSave(
    named,
    useZoneCoreStore.getState().drafts[field.field_id] ?? [],
  );
  const saved = await api.saveZones(field.field_id, named, cores);
  if (cores.length) {
    const versionId = useZoneCoreStore
      .getState()
      .commit(field.field_id, cores, saved.zone_version_id);
    const layerId = zoneCoresLayerId(versionId);
    const existing = useLayerStore.getState().byId[layerId];
    if (existing) useLayerStore.getState().setVisible(layerId, true);
    else
      useLayerStore.setState((s) => ({
        byId: {
          ...s.byId,
          [layerId]: { visible: true, opacity: 1, order: 206 },
        },
      }));
  } else useZoneCoreStore.getState().clearDraft(field.field_id);
  if (action === "order") {
    await api.orderSampling(field.field_id, {
      config: {
        kind: "zones",
        zone_count: named.features.length,
        ...(saved.zone_version_id
          ? { zone_version_id: saved.zone_version_id }
          : {}),
        lab: labForWire(lab),
        ...(cores.length ? { cores } : {}),
      },
    });
  }
  return registerTarget(
    field.field_id,
    "zones",
    named,
    action === "order",
    when,
    { id: saved.zone_version_id },
  );
}

// Persist a list of RESOLVED designs (from resolvePresetFCs / resolveCampaignFCs) in the chosen mode.
// Sequential, never throws; per-field errors collected so a partial success reports which fields landed.
// Marks each field `done` so a RETRY after a partial failure never re-commits (no duplicate work orders).
export async function persistResolvedFCs(
  resolved: ResolvedFC[],
  kind: "points" | "zones",
  action: SamplingMode,
  lab: LabOrderConfig,
  when: string,
): Promise<FieldOrderResult[]> {
  const out: FieldOrderResult[] = [];
  for (const r of resolved) {
    if (r.done) {
      out.push({ field: r.field, ok: true, layerId: r.layerId });
      continue;
    } // already committed on a prior attempt
    try {
      if (!r.fc || !r.fc.features?.length)
        throw new Error("no design available (open it in the stepper)");
      const layerId = await persistFieldFC(
        r.field,
        kind,
        r.fc,
        lab,
        action,
        when,
      );
      r.done = true;
      r.layerId = layerId;
      out.push({ field: r.field, ok: true, layerId });
    } catch (e) {
      out.push({ field: r.field, ok: false, error: errMsg(e) });
    }
  }
  return out;
}

// The sample/zone COUNT of a resolved design (for the confirm dialog's price breakdown).
export function fcCount(fc: GeoJSON.FeatureCollection | null): number {
  return fc?.features?.length ?? 0;
}

export { campaignKind };
